import { useState } from "react";
import { useOAuth2Flow } from "@/hooks/useOAuth2Flow";
import { useOAuth2Providers } from "@/hooks/useOAuth2Providers";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { isOAuthProviderVisible } from "@/lib/oauth2";

interface OAuthButtonsProps {
  isLinking?: boolean;
  isSignUp?: boolean;
  onError?: (error: Error) => void;
}

const PROVIDER_ICONS: Record<string, string> = {
  wiaccount: "Wi",
  "localhost-oauth": "Tp",
};

export function OAuthButtons({
  isLinking = false,
  isSignUp = false,
  onError,
}: OAuthButtonsProps) {
  const { initiateOAuth2, error, clearError } = useOAuth2Flow();
  // Use `isPending` (status-based) rather than `isLoading` (= isPending && isFetching).
  // isLoading toggles false during React Query retry delays, which made the providers
  // spinner blink on/off when the providers request was slow or failing. isPending stays
  // true until data arrives or the query errors, so the initial-load spinner is stable.
  const { data: allProviders, isPending: providersLoading } =
    useOAuth2Providers();
  // Local-only providers are dropped before every branch below, so a deployed environment whose
  // only remaining provider was filtered out still reports "no providers" rather than rendering
  // an empty list.
  const providers = allProviders?.filter((provider) =>
    isOAuthProviderVisible(provider.name),
  );
  const { t } = useTranslation(["auth", "errors"]);
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);

  const handleClick = async (providerName: string) => {
    setLoadingProvider(providerName);
    clearError();
    try {
      await initiateOAuth2(providerName);
      // Navigation is in progress — keep loading state until the page unloads.
      // Do NOT clear loadingProvider here; the spinner must persist while the
      // browser navigates to the OAuth provider (window.location.href redirect).
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Failed to initiate OAuth2 flow");
      const isNetworkError =
        !navigator.onLine || error.message === "Network Error";
      toast.error(
        isNetworkError ? t("errors:auth.networkError") : error.message,
      );
      onError?.(error);
      // Only clear loading on error; on success the page navigates away.
      setLoadingProvider(null);
    }
  };

  if (providersLoading) {
    return (
      <div className="flex justify-center py-4">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand border-t-transparent will-change-transform" />
      </div>
    );
  }

  if (!providers || providers.length === 0) {
    return (
      <p className="text-sm text-destructive text-center">
        {t("login.oauth.noProviders")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {providers.map((provider) => {
        const isThisLoading = loadingProvider === provider.name;
        const anyLoading = loadingProvider !== null;
        const icon =
          PROVIDER_ICONS[provider.name] ||
          provider.displayName.charAt(0).toUpperCase();
        const label = isThisLoading
          ? t("login.oauth.redirecting")
          : isLinking
            ? t("login.oauth.linkAccountWith", {
                provider: provider.displayName,
              })
            : isSignUp
              ? t("login.oauth.signUpWithProvider", {
                  provider: provider.displayName,
                })
              : t("login.oauth.continueWithProvider", {
                  provider: provider.displayName,
                });

        return (
          <button
            key={provider.name}
            type="button"
            onClick={() => handleClick(provider.name)}
            disabled={anyLoading}
            className={cn(
              "w-full flex items-center justify-center gap-2.5 h-12 rounded-xl text-white text-[14.5px] font-medium",
              "bg-brand transition-[background-color,color,transform] duration-150",
              "hover:-translate-y-px hover:bg-brand-hover",
              "active:translate-y-0",
              "disabled:pointer-events-none",
              // Active button stays full opacity; other buttons dim but don't look "loading"
              isThisLoading ? "opacity-100" : anyLoading ? "opacity-40" : "",
              // Drop the heavy blurred glow while the spinner is active so the rotating
              // icon isn't repainted together with an expensive 24px-blur shadow each frame.
              isThisLoading
                ? "shadow-[0_1px_0_rgba(255,255,255,.18)_inset]"
                : "shadow-[0_1px_0_rgba(255,255,255,.18)_inset,0_8px_24px_var(--brand-soft)]",
            )}
          >
            {isThisLoading ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent shrink-0 will-change-transform" />
            ) : (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-[5px] bg-white text-brand font-bold text-xs tracking-tight shrink-0">
                {icon}
              </span>
            )}
            {label}
          </button>
        );
      })}

      {error && <p className="text-sm text-destructive text-center">{error}</p>}
    </div>
  );
}
